import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import {Typography, StepContent} from '@material-ui/core';
import {toFormattedDateTime} from 'Shared/utilities/DateFormatter';
import './GenelcoSpreadsheetFileUploadDetails.sass';
import {getEnrollmentApplicationsDetail} from 'Enrollment/ducks/enrollmentFileProcessing';
import {ReducerState} from 'rootReducer';
import {useParams} from 'react-router-dom';
import {renderAsyncView} from 'Shared/components/RenderAsync';
import {GenelcoSpreadsheetFileUploadSteps} from 'Shared/constants/GenelcoSpreadsheetFileUploadSteps';
import {EnrollmentFileStatusType} from 'Enrollment/models/EnrollmentFileStatusType';
import {EnrollmentApplication} from 'Enrollment/models/EnrollmentApplication';
import GenelcoSpreadsheetFileSummaryAccordion from './GenelcoSpreadsheetFileSummaryAccordion';
import * as NEA from 'fp-ts/lib/NonEmptyArray';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import Loading from 'Shared/components/Loading';
import {EnrollmentFileStatus} from 'Enrollment/models/EnrollmentFilesNotProcessed';
import GenelcoSpreadsheetHorizontalStatusTracker from './HorizontalStatusTracker/GenelcoSpreadsheetHorizontalStatusTracker';
import {ExternalEnrollmentFileUploadSteps} from 'Shared/constants/ExternalEnrollmentFileUploadSteps';
import CancelIcon from '@material-ui/icons/Cancel';


const steps = [
  {id: 0, value: 'File Uploaded'},
  {id: 1, value: 'Received'},
  {id: 2, value: 'Processed'},
  {id: 3, value: 'Confirmed'},
];

const GenelcoSpreadsheetFileUploadDetails = () => {
  const {enrollmentApplicationsDetails, isFileUploading} = useSelector(
    (state: ReducerState) => state.enrollmentFileProcessing
  );
  const dispatch = useDispatch();
  const {fileId} = useParams();
  const isFileUploadBefore = (enrollmentFile1: EnrollmentApplication, enrollmentFile2: EnrollmentApplication) =>
    new Date(enrollmentFile2.fileUploadedDate!).getTime() - new Date(enrollmentFile1.fileUploadedDate!).getTime();

  useEffect(() => {
    dispatch(getEnrollmentApplicationsDetail(fileId!));
  }, [dispatch, fileId]);

  const getActiveStepperClass = (
    index: number,
    validRecordCount: number,
    invalidRecordCount: number,
    rowCount: number,
    status: EnrollmentFileStatus,
    validAuditCount: number,
    invalidAuditCount: number,
    groupWebComparisonErrorCount: number
  ) => {
    if (status === EnrollmentFileStatusType.RECEIVED || status === EnrollmentFileStatusType.VALIDATION_COMPLETE) 
    {
      if(rowCount === validRecordCount)
        return 'stepper__completed';
      else if(rowCount === invalidRecordCount)
        return 'stepper__error';
      else if(rowCount !== invalidRecordCount && invalidRecordCount > 0)
        return 'stepper__warning';
      else  
        return 'stepper__active';
      
    }
    else if (status === EnrollmentFileStatusType.PROCESSING || status === EnrollmentFileStatusType.READY_FOR_AUDIT) 
    {
      return 'stepper__active';
    } 
    else if (status === EnrollmentFileStatusType.AUDIT_COMPLETE || status === EnrollmentFileStatusType.KICKOUT_VALIDATION_COMPLETE)
    {
      if(rowCount === validAuditCount)
        return 'stepper__completed';
      else if(rowCount === invalidAuditCount)
        return 'stepper__error';
      else if(rowCount !== invalidAuditCount && invalidAuditCount > 0)
        return 'stepper__warning';
      else  
        return 'stepper__active';
    } 
    else if (status === EnrollmentFileStatusType.CANCELED)
    {
      return 'stepper__canceled';
    } 
    else if (status === EnrollmentFileStatusType.COMPLETED)
    {
      if(validAuditCount > 0)
        return 'stepper__active';
      else  
        return 'stepper__inactive';
    } 
    else if (status === EnrollmentFileStatusType.GROUPWEB_AUDIT_COMPLETE)
    {
      if(validAuditCount > 0 && invalidAuditCount > 0 )
        return 'stepper__warning';
      if(rowCount === validAuditCount)
        return 'stepper__completed';
      else if(rowCount === invalidAuditCount ||  validAuditCount === groupWebComparisonErrorCount)
        return 'stepper__error';
      else  
        return 'stepper__inactive';
    } 
    else
    {
      return 'stepper__inactive'; 
    }
  };

  const getActiveIcon = (
    index: number,
    validRecordCount: number,
    invalidRecordCount: number,
    rowCount: number,
    status: EnrollmentFileStatus,
    validAuditCount: number,
    invalidAuditCount: number,
    groupWebComparisonErrorCount: number
  ) => {
    if (index === GenelcoSpreadsheetFileUploadSteps.FileUpload)
    {
      if(rowCount === validRecordCount)
        return 'fas fa-check-circle status__successful';
      else if(rowCount === invalidRecordCount)
        return 'fas fa-times-circle status__unsuccessful';
      else if(validRecordCount > 0  && invalidRecordCount > 0)
        return 'fas fa-exclamation-triangle status__warning';
      else  
        return 'far fa-circle status__inactive';
    }
    else if (index === GenelcoSpreadsheetFileUploadSteps.Received)
    {
      if(validRecordCount > 0)
        return 'fas fa-check-circle status__successful';
      else if(rowCount === invalidRecordCount)
        return 'fas fa-times-circle status__unsuccessful';
      else  
        return 'far fa-circle status__inactive';
    }
    else if (status === EnrollmentFileStatusType.READY_FOR_AUDIT) 
    {
      if(index === GenelcoSpreadsheetFileUploadSteps.ProcessingComplete)
      {
        return 'far fa-circle status__inactive';
      }
      return 'far fa-circle status__processing';
    } 
    else if (status === EnrollmentFileStatusType.CANCELED)
    {
      if(index === GenelcoSpreadsheetFileUploadSteps.ProcessingComplete)
      {
        return 'far fa-circle status__inactive';
      }
      return 'fas fa-times-circle status__canceled';
    } 
    else if (index === GenelcoSpreadsheetFileUploadSteps.Processing )
    {
      
      if(validAuditCount > 0 && invalidAuditCount > 0 && (status === EnrollmentFileStatusType.KICKOUT_VALIDATION_COMPLETE || status === EnrollmentFileStatusType.AUDIT_COMPLETE) )
          return 'fas fa-exclamation-triangle status__warning'; 
      else if((validRecordCount === validAuditCount) && (status === EnrollmentFileStatusType.KICKOUT_VALIDATION_COMPLETE || status === EnrollmentFileStatusType.AUDIT_COMPLETE ) )
          return 'fas fa-check-circle status__successful'; 
      else if((validRecordCount === invalidAuditCount) && (status === EnrollmentFileStatusType.KICKOUT_VALIDATION_COMPLETE || status === EnrollmentFileStatusType.AUDIT_COMPLETE ) )
        return 'fas fa-times-circle status__unsuccessful';
      
      else if(validAuditCount > 0 && invalidAuditCount > 0 && (status === EnrollmentFileStatusType.COMPLETED ))
        return 'fas fa-exclamation-triangle status__warning';
      else if((validRecordCount === validAuditCount) && (status === EnrollmentFileStatusType.COMPLETED ))
        return 'fas fa-check-circle status__successful';    
      else if((validRecordCount === invalidAuditCount) && (status === EnrollmentFileStatusType.COMPLETED ))
        return 'fas fa-times-circle status__unsuccessful';

      else if(validAuditCount > 0 && invalidAuditCount > 0 && status === EnrollmentFileStatusType.GROUPWEB_AUDIT_COMPLETE)
        return 'fas fa-exclamation-triangle status__warning';
      else if(validRecordCount === validAuditCount && status === EnrollmentFileStatusType.GROUPWEB_AUDIT_COMPLETE)
        return 'fas fa-check-circle status__successful';   
      else if(validAuditCount === groupWebComparisonErrorCount  && status === EnrollmentFileStatusType.GROUPWEB_AUDIT_COMPLETE)
        return 'fas fa-times-circle status__unsuccessful';
      else if(validRecordCount === invalidAuditCount && status === EnrollmentFileStatusType.GROUPWEB_AUDIT_COMPLETE)
        return 'fas fa-times-circle status__unsuccessful';

      else  
          return 'far fa-circle status__inactive';
    }
    else if(index === GenelcoSpreadsheetFileUploadSteps.ProcessingComplete )
    {
      if((validAuditCount > 0) && (status === EnrollmentFileStatusType.COMPLETED ))
        return 'far fa-circle status__processing';
      else if((validAuditCount === 0) && (status === EnrollmentFileStatusType.COMPLETED ))
        return 'far fa-circle status__inactive';       
      else if(validRecordCount === validAuditCount && status === EnrollmentFileStatusType.GROUPWEB_AUDIT_COMPLETE)
        return 'fas fa-check-circle status__successful'; 
      else if(validAuditCount > 0 && invalidAuditCount > 0 && status === EnrollmentFileStatusType.GROUPWEB_AUDIT_COMPLETE)
        return 'fas fa-exclamation-triangle status__warning';  
      else if(validAuditCount === groupWebComparisonErrorCount  && status === EnrollmentFileStatusType.GROUPWEB_AUDIT_COMPLETE)
        return 'fas fa-times-circle status__unsuccessful';
      else if(validRecordCount === invalidAuditCount && status === EnrollmentFileStatusType.GROUPWEB_AUDIT_COMPLETE)
        return 'fas fa-times-circle status__unsuccessful';
      else  
        return 'far fa-circle status__inactive';
    }
    else
    {
      return 'stepper__inactive'; 
    }
  };
  
  const getVerticalStepStatusDetails = (enrollmentFile: EnrollmentApplication) => {
    const messageSuccess = 'Successful';
    const messageUnsuccess = 'Unsuccessful';
    const verticalSteps = steps.map(step => {
      if (step.id === GenelcoSpreadsheetFileUploadSteps.FileUpload) {
        return `${step.value} - ${messageSuccess} - ${toFormattedDateTime(new Date(enrollmentFile.fileUploadedDate!))}`;
      } 
      else if (step.id === GenelcoSpreadsheetFileUploadSteps.Received) {
        if (enrollmentFile.rowCount! > 0 && enrollmentFile.validRecordCount! > 0) {
          return `${step.value} - ${messageSuccess} - ${toFormattedDateTime(
            new Date(enrollmentFile.fileUploadedDate!)
          )}`;
        } else {
          return `${step.value} - ${messageUnsuccess} - ${toFormattedDateTime(
            new Date(enrollmentFile.fileUploadedDate!)
          )}`;
        }
      } 
      else if (
        enrollmentFile.fileStatus?.id! === EnrollmentFileStatusType.CANCELED &&
        getProcessingSteps(enrollmentFile?.fileStatusIdBeforeCanceled!) === step.id
      )
        return `${step.value} -  ${toFormattedDateTime(enrollmentFile.fileCanceledDate!)}`;
      else if (step.id === GenelcoSpreadsheetFileUploadSteps.Processing) 
      {
        if (enrollmentFile.fileStatus!.id === EnrollmentFileStatusType.READY_FOR_AUDIT) 
        {
          return `${step.value} - ${toFormattedDateTime(new Date(enrollmentFile.fileUploadedDate!))}`;
        } 
        else if (
          enrollmentFile.fileStatus!.id === EnrollmentFileStatusType.KICKOUT_VALIDATION_COMPLETE ||
          enrollmentFile.fileStatus!.id === EnrollmentFileStatusType.AUDIT_COMPLETE
        ) 
        {
          const validationCompletedDate = enrollmentFile.validationCompletedDate
            ? enrollmentFile.validationCompletedDate
            : enrollmentFile.fileUploadedDate;
          if (enrollmentFile.invalidAuditCount! > 0) 
          {
            return `${step.value} - ${messageUnsuccess} - ${toFormattedDateTime(new Date(validationCompletedDate!))}`;
          } 
          else 
          {
            return `${step.value} - ${messageSuccess} - ${toFormattedDateTime(new Date(validationCompletedDate!))}`;
          }
        }
        else if(enrollmentFile.invalidAuditCount! > 0)
        {
          const validationCompletedDate = enrollmentFile.validationCompletedDate
            ? enrollmentFile.validationCompletedDate
            : enrollmentFile.fileUploadedDate;
          return `${step.value} - ${messageUnsuccess} - ${toFormattedDateTime(new Date(validationCompletedDate!))}`;
        }
        else if(enrollmentFile.validAuditCount! === enrollmentFile.validRecordCount!)
        {
          const validationCompletedDate = enrollmentFile.validationCompletedDate
            ? enrollmentFile.validationCompletedDate
            : enrollmentFile.fileUploadedDate;
          return `${step.value} - ${messageSuccess} - ${toFormattedDateTime(new Date(validationCompletedDate!))}`;
        }  
        else 
        {
          return step.value;
        }
      } 
      else if (step.id === GenelcoSpreadsheetFileUploadSteps.ProcessingComplete) 
      {
        if (enrollmentFile.fileStatus!.id! === EnrollmentFileStatusType.COMPLETED) 
        {
          if(enrollmentFile.validAuditCount! > 0)
          {
            return `${step.value} - ${toFormattedDateTime(
              enrollmentFile.processingCompletedDate!
            )}`;
          }
          else
          {
            return step.value;
          }
          
        }
        else if (enrollmentFile.fileStatus!.id! === EnrollmentFileStatusType.GROUPWEB_AUDIT_COMPLETE) 
        {
          
          if (enrollmentFile.groupWebComparisonCompleteDate && enrollmentFile.groupWebComparisonErrorCount! > 0) 
          {
            return `${step.value} - ${messageUnsuccess} - ${toFormattedDateTime(
              enrollmentFile.groupWebComparisonCompleteDate!
            )}`;
          }
          else if(enrollmentFile.invalidAuditCount! > 0 || enrollmentFile.invalidRecordCount! > 0)
          {
            return `${step.value} - ${messageUnsuccess} - ${toFormattedDateTime(
              enrollmentFile.groupWebComparisonCompleteDate!
            )}`;
          }
          else if (enrollmentFile.groupWebComparisonCompleteDate && enrollmentFile.groupWebComparisonErrorCount! === 0) 
          {
            return `${step.value} - ${messageSuccess} - ${toFormattedDateTime(
              enrollmentFile.groupWebComparisonCompleteDate!
            )}`;
          } 
        }  
        else 
        {
          return step.value;
        }
      } 
      else 
      {
        return step.value;
      }
      return step.value;
    });
    return verticalSteps;
  };

  const getCompletedStatusClass = (
    statusId: number,
    stepIndex: number,
    invalidAuditCount: number,
    groupWebComparisonErrorCount: number,
    rowCount: number,
    validAuditCount: number
  ) => {
    if (stepIndex === GenelcoSpreadsheetFileUploadSteps.Processing || stepIndex === GenelcoSpreadsheetFileUploadSteps.ProcessingComplete )
    {
      if(validAuditCount > 0 && invalidAuditCount > 0)
        return 'stepper__warning';
      if(rowCount === validAuditCount)
        return 'stepper__completed';
      else if(rowCount === invalidAuditCount)
        return 'stepper__error';
      else  
        return 'stepper__inactive';
    }
    else 
    {
      return 'stepper__completed';
    }
    
  };

  const getActiveSteps = (id: number, fileStatusTypeBeforeCanceled: number) => {
    switch (id) {
      case EnrollmentFileStatusType.RECEIVED:
        return GenelcoSpreadsheetFileUploadSteps.Received;
      case EnrollmentFileStatusType.VALIDATION_COMPLETE:
        return GenelcoSpreadsheetFileUploadSteps.Received;
      case EnrollmentFileStatusType.READY_FOR_AUDIT:
        return GenelcoSpreadsheetFileUploadSteps.Processing;
      case EnrollmentFileStatusType.AUDIT_COMPLETE:
        return GenelcoSpreadsheetFileUploadSteps.Processing;
      case EnrollmentFileStatusType.KICKOUT_VALIDATION_COMPLETE:
        return GenelcoSpreadsheetFileUploadSteps.Processing;
      case EnrollmentFileStatusType.COMPLETED:
      case EnrollmentFileStatusType.GROUPWEB_AUDIT_COMPLETE:  
        return GenelcoSpreadsheetFileUploadSteps.ProcessingComplete;
      case EnrollmentFileStatusType.CANCELED:
        return getCanceledSteps(fileStatusTypeBeforeCanceled);
      default:
        return 0;
    }
  };

  const getProcessingSteps = (id: number) => {
    switch (id) {
      case EnrollmentFileStatusType.PROCESSING:
      case EnrollmentFileStatusType.KICKOUT_VALIDATION_COMPLETE:
        return ExternalEnrollmentFileUploadSteps.Processing;
      case EnrollmentFileStatusType.COMPLETED:
      case EnrollmentFileStatusType.GROUPWEB_AUDIT_COMPLETE:
        return ExternalEnrollmentFileUploadSteps.Completed;
      default:
        return 0;
    }
  };

  const getCanceledSteps = (fileStatusTypeBeforeCanceled: number) => {
    switch (fileStatusTypeBeforeCanceled) {
      case EnrollmentFileStatusType.RECEIVED:
        return ExternalEnrollmentFileUploadSteps.Received;
      case EnrollmentFileStatusType.PROCESSING:
      case EnrollmentFileStatusType.KICKOUT_VALIDATION_COMPLETE:
      case EnrollmentFileStatusType.READY_FOR_AUDIT:
        return ExternalEnrollmentFileUploadSteps.Processing;
      case EnrollmentFileStatusType.COMPLETED:
        return ExternalEnrollmentFileUploadSteps.ProcessingComplete;
    }
  };

  return renderAsyncView(enrollmentApplicationsDetails, details => {
    if (isFileUploading) {
      return <Loading />;
    }

    return (
      <div className="genelco-spreadsheet-file">
        {pipe(
          details,
          NEA.fromArray,
          O.map(enrollmentFiles => {
            const enrollments = enrollmentFiles.sort(isFileUploadBefore);
            const latestEnrollmentFile = NEA.head(enrollments);
            const enrollmentFileHistory = NEA.tail(enrollments);
            return (
              <>
                <GenelcoSpreadsheetHorizontalStatusTracker
                  enrollmentFile={latestEnrollmentFile}
                  steps={steps.map(step =>
                    step.id === ExternalEnrollmentFileUploadSteps.ProcessingComplete
                      ? {...step, value: 'Confirmation'}
                      : step
                  )}
                  currentStep={getActiveSteps(
                    latestEnrollmentFile.fileStatus!.id!,
                    latestEnrollmentFile.fileStatusIdBeforeCanceled!
                  )}
                />
                <div className="genelco-spreadsheet-file__content-container">
                  <GenelcoSpreadsheetFileSummaryAccordion
                    enrollmentFile={latestEnrollmentFile}
                    enrollmentFileHistory={enrollmentFileHistory}
                    fileId={fileId!}
                  />
                  <div className="section-divider-container">
                    <Typography className="section-divider-container__title" variant="h6">
                      File Status
                    </Typography>
                  </div>
                  <div className="vertical-stepper-info">
                    <Stepper
                      activeStep={getActiveSteps(
                        latestEnrollmentFile.fileStatus!.id!,
                        latestEnrollmentFile.fileStatusIdBeforeCanceled!
                      )}
                      orientation="vertical"
                    >
                      {getVerticalStepStatusDetails(latestEnrollmentFile).map((label, index) => (
                        <Step key={`vertical-stepper-${index}`}>
                          <StepLabel
                            StepIconProps={{
                              classes: {
                                active: getActiveStepperClass(
                                  index,
                                  latestEnrollmentFile.validRecordCount!,
                                  latestEnrollmentFile.invalidRecordCount!,
                                  latestEnrollmentFile.rowCount!,
                                  latestEnrollmentFile.fileStatus!.id as EnrollmentFileStatus,
                                  latestEnrollmentFile.validAuditCount!,
                                  latestEnrollmentFile.invalidAuditCount!,
                                  latestEnrollmentFile.groupWebComparisonErrorCount!
                                ),
                                completed: getCompletedStatusClass(
                                  latestEnrollmentFile.fileStatus!.id!,
                                  index,
                                  latestEnrollmentFile.invalidAuditCount!,
                                  latestEnrollmentFile.groupWebComparisonErrorCount!,
                                  latestEnrollmentFile.rowCount!,
                                  latestEnrollmentFile.validAuditCount!
                                ),
                              },
                              icon: (
                                <span className="icon">
                                  <i
                                    className={getActiveIcon(
                                      index,
                                      latestEnrollmentFile.validRecordCount!,
                                      latestEnrollmentFile.invalidRecordCount!,
                                      latestEnrollmentFile.rowCount!,
                                      latestEnrollmentFile.fileStatus!.id as EnrollmentFileStatus,
                                      latestEnrollmentFile.validAuditCount!,
                                      latestEnrollmentFile.invalidAuditCount!,
                                      latestEnrollmentFile.groupWebComparisonErrorCount!
                                    )}
                                  />
                                </span>
                              ),
                            }}
                          >
                            {label}
                          </StepLabel>
                          <StepContent>
                            {latestEnrollmentFile?.fileStatus?.id! === EnrollmentFileStatusType.CANCELED &&
                              getCanceledSteps(latestEnrollmentFile?.fileStatusIdBeforeCanceled!) === index && (
                                <span>
                                  {' '}
                                  <CancelIcon className="vertical-cancel__icon" /> Cancelled -{' '}
                                  {toFormattedDateTime(latestEnrollmentFile?.fileCanceledDate!)}{' '}
                                </span>
                              )}
                          </StepContent>

                          {latestEnrollmentFile.batchReGeneratoinDateHistory?.map(value => (
                            <StepContent>
                              {(latestEnrollmentFile?.fileStatus?.id! === EnrollmentFileStatusType.COMPLETED ||
                                latestEnrollmentFile?.fileStatus?.id! ===
                                  EnrollmentFileStatusType.GROUPWEB_AUDIT_COMPLETE) &&
                                latestEnrollmentFile.batchHasReGenerated && (
                                  <span> AB is re-starting confirmed step - {toFormattedDateTime(value!)} </span>
                                )}
                            </StepContent>
                          ))}

                          {latestEnrollmentFile.batchReAuditDateHistory?.map(value => (
                            <StepContent>
                              {(latestEnrollmentFile?.fileStatus?.id! === EnrollmentFileStatusType.COMPLETED ||
                                latestEnrollmentFile?.fileStatus?.id! ===
                                  EnrollmentFileStatusType.GROUPWEB_AUDIT_COMPLETE) &&
                                latestEnrollmentFile.batchHasReGenerated && (
                                  <span>
                                    {' '}
                                    AB is re-auditing the records in confirmed step - {toFormattedDateTime(value!)}{' '}
                                  </span>
                                )}
                            </StepContent>
                          ))}
                        </Step>
                      ))}
                    </Stepper>
                  </div>
                </div>
              </>
            );
          }),
          O.getOrElse(() => <div className="">No File Information Available</div>)
        )}
      </div>
    );
  });
};

export default GenelcoSpreadsheetFileUploadDetails;
